import type { CSSProperties } from 'react';

/**
 * =============================================================================
 * MARCA — Tom Studio · Manual de marca v1 (2026)
 * =============================================================================
 * Reemplaza el logotipo anterior ("T●M" con la O como disco amarillo), que no
 * estaba en el manual.
 *
 * El símbolo es un TILE: una retícula 2×2 con las cuatro formas primarias de la
 * Bauhaus. Las posiciones NUNCA cambian —círculo arriba-izq., triángulo
 * arriba-der., blanco abajo-izq., cuadrado abajo-der.— y el radio de esquina es
 * el 20% del lado. Tamaño mínimo 24 px en pantalla.
 *
 * Salonika es el producto "tom restaurante" instalado donde un cliente, así que
 * usa la LÍNEA AZUL del producto y se firma `tom | salonika` (sección 08 del
 * manual: "El producto se nombra tom + industria en su línea de color… Instalado
 * donde un cliente, se firma tom | cliente sobre el color del cliente").
 *
 * Tipografía del wordmark: Jost, minúsculas, weight 500–600 (sección 04). El
 * import está en styles/index.css.
 *
 * Lo que el manual prohíbe (sección 05) y por eso no se parametriza: deformar,
 * rotar, recolorear y reordenar las formas. Por eso el tile no acepta props de
 * color — solo de tamaño.
 * =============================================================================
 */

/** Web del estudio (manual, sección 07: "tomstudio.co · Bogotá"). */
export const TOM_URL = 'https://tomstudio.co';

// --- Paleta del manual (sección 03) -----------------------------------------
export const TOM_NAVY     = '#14151A';
export const TOM_AZUL     = '#1B63C4';
export const TOM_AMARILLO = '#F5B301';
export const TOM_ROJO     = '#E1251B';
export const TOM_PAPEL    = '#F4F2EA';
export const TOM_TINTA    = '#14171C';

/** Compat: algún componente viejo importaba TOM_YELLOW. */
export const TOM_YELLOW = TOM_AMARILLO;

// --- Línea de color del producto "tom restaurante" (sección 08) --------------
const LINEA_BASE      = '#0E2A5E';  // fondo del tile
const LINEA_CIRCULO   = '#1E63C8';  // círculo (arriba-izq.)
const LINEA_CUADRADO  = '#3E86E8';  // cuadrado (abajo-der.) — el rojo del
                                    // estudio se sustituye por la línea azul
// El nombre del CLIENTE va en su color. Para Salonika es el amarillo del
// manual: la fórmula de la sección 08 muestra `tom | cliente` con el cliente en
// #F5B301. (La primera versión de esto tomó el ejemplo de la sección 06, que
// usa el azul claro de la línea del producto — ese es otro caso.)
const LINEA_NOMBRE    = TOM_AMARILLO;

const JOST = 'Jost, "Plus Jakarta Sans", sans-serif';

interface TileProps {
  /** Lado en px. El manual fija 24 px como mínimo en pantalla. Default: 44. */
  size?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * TomTile — el símbolo. Retícula 2×2 con las cuatro formas.
 * Todo se deriva del lado para que escale sin deformarse.
 */
export function TomTile({ size = 44, className = '', style }: TileProps) {
  const q = size / 2;                 // cuadrante
  const r = Math.round(size * 0.2);   // radio de esquina = 20% del lado

  const cuadrante: CSSProperties = { position: 'absolute', width: q, height: q };

  return (
    <div
      className={className}
      role="img"
      aria-label="Tom Studio"
      style={{
        width: size,
        height: size,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: r,
        background: LINEA_BASE,
        flexShrink: 0,
        ...style,
      }}
    >
      {/* Círculo — arriba-izquierda. Lo continuo: el software. */}
      <div style={{ ...cuadrante, top: 0, left: 0, borderRadius: `0 0 ${q}px 0`, background: LINEA_CIRCULO }} />
      {/* Triángulo — arriba-derecha. Lo activo: el marketing. */}
      <div
        style={{
          position: 'absolute', top: 0, right: 0, width: 0, height: 0,
          borderLeft: `${q}px solid transparent`,
          borderTop: `${q}px solid ${TOM_AMARILLO}`,
        }}
      />
      {/* Cuadrado — abajo-derecha. Lo estable: la ingeniería. */}
      <div style={{ ...cuadrante, bottom: 0, right: 0, background: LINEA_CUADRADO }} />
      {/* Blanco — abajo-izquierda. */}
      <div style={{ ...cuadrante, bottom: 0, left: 0, borderRadius: `0 ${q}px 0 0`, background: '#fff' }} />
    </div>
  );
}

/**
 * SalonikaTomIcon — icono de app. Es el tile, sin adornos.
 * Se conserva el nombre del export para no tocar los cuatro componentes que ya
 * lo consumen (login, top-bar, sidebar, factura pública).
 */
export function SalonikaTomIcon({ size = 84, className = '', style }: TileProps) {
  return <TomTile size={size} className={className} style={style} />;
}

interface LockupProps {
  /** Font-size en px del wordmark. Default: 22. */
  size?: number;
  /** El símbolo acompaña SIEMPRE al lockup. Solo se apaga donde el tile ya se
   *  está dibujando aparte (evita verlo dos veces). */
  conTile?: boolean;
  /** Color de "tom". Default: blanco (todos los usos actuales van sobre navy). */
  tomColor?: string;
  /** Color de "salonika" — el color del cliente. */
  accent?: string;
  /** Color del divisor vertical. */
  dividerColor?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * TomSalonikaLockup — la firma del producto instalado en un cliente:
 * `tom | salonika`. Jost en minúsculas: "tom" en 600, el nombre del cliente
 * en 500 sobre su color (sección 08 del manual).
 */
export function TomSalonikaLockup({
  size = 22,
  conTile = true,
  tomColor = '#fff',
  accent = LINEA_NOMBRE,
  dividerColor = 'rgba(255,255,255,.4)',
  className = '',
  style,
}: LockupProps) {
  // El amarillo del manual sobre el azul de la app da 3.5:1 de contraste:
  // alcanza para "texto grande" (WCAG: ≥24px, o ≥18.7px en negrita) pero no
  // para texto chico. En vez de aclarar el color de marca —que no se toca— el
  // wordmark sube a 600 cuando es chico, y ahí ya califica como texto grande.
  // El manual admite 500–600 para el wordmark, así que sigue dentro de norma.
  const pesoNombre = size < 24 ? 600 : 500;
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: size * 0.42,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        fontFamily: JOST,
        ...style,
      }}
    >
      {conTile && <TomTile size={size * 1.55} />}
      <span aria-hidden="true" style={{ fontSize: size, fontWeight: 600, color: tomColor, lineHeight: 1 }}>
        tom
      </span>
      <span
        aria-hidden="true"
        style={{
          width: 1.5,
          height: size * 0.76,
          background: dividerColor,
          flexShrink: 0,
        }}
      />
      <span aria-hidden="true" style={{ fontSize: size, fontWeight: pesoNombre, color: accent, lineHeight: 1 }}>
        salonika
      </span>
      <span className="sr-only">tom salonika</span>
    </span>
  );
}

/**
 * TomStudioLockup — la marca del estudio: `tom studio`. Se usa en el pie.
 * "tom" en 600 y "studio" en 400, como en la sección 02 del manual.
 */
export function TomStudioLockup({
  size = 17,
  conTile = true,
  tomColor = '#fff',
  accent = 'rgba(255,255,255,.62)',
  className = '',
  style,
}: Omit<LockupProps, 'dividerColor'>) {
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: size * 0.5,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        fontFamily: JOST,
        ...style,
      }}
    >
      {conTile && <TomTile size={size * 1.7} />}
      <span aria-hidden="true" style={{ fontSize: size, fontWeight: 600, color: tomColor, lineHeight: 1 }}>
        tom <span style={{ fontWeight: 400, color: accent }}>studio</span>
      </span>
      <span className="sr-only">tom studio</span>
    </span>
  );
}

interface FooterProps {
  /** En sidebar colapsado se compacta: solo el tile. */
  collapsed?: boolean;
  mutedColor?: string;
  tomColor?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * TomFooter — pie de marca.
 *
 * Antes decía "funciona con T●M" + "Tom Marketing & Software". Ese lockup no
 * existe en el manual: la marca del estudio es `tom studio` con el descriptor
 * "Marketing & Software" debajo (secciones 02 y 06). Se reemplaza por eso.
 */
export function TomFooter({
  collapsed = false,
  mutedColor = 'rgba(255,255,255,0.62)',
  tomColor = '#fff',
  className = '',
  style,
}: FooterProps) {
  if (collapsed) {
    return (
      <a
        href={TOM_URL}
        target="_blank"
        rel="noopener noreferrer"
        title="Tom Studio · Marketing & Software"
        className={`flex justify-center transition-opacity hover:opacity-80 ${className}`}
        style={{ textDecoration: 'none', ...style }}
      >
        <TomTile size={26} />
      </a>
    );
  }
  return (
    <a
      href={TOM_URL}
      target="_blank"
      rel="noopener noreferrer"
      title="Tom Studio · Marketing & Software"
      className={`flex flex-col transition-opacity hover:opacity-80 ${className}`}
      style={{ textDecoration: 'none', gap: 5, ...style }}
    >
      <TomStudioLockup size={16} tomColor={tomColor} accent={mutedColor} />
      <span
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '.02em',
          color: mutedColor,
          fontFamily: JOST,
        }}
      >
        Marketing &amp; Software
      </span>
    </a>
  );
}

/**
 * TomLogo — compat. Antes era el logotipo "T●M". Ese logotipo no está en el
 * manual; se mantiene el export para no romper imports viejos, pero renderiza
 * el wordmark correcto (`tom` en Jost) en vez del disco amarillo.
 */
export function TomLogo({
  size = 22,
  color = 'currentColor',
  className = '',
  style,
}: { size?: number; color?: string; discColor?: string; className?: string; style?: CSSProperties }) {
  return (
    <span
      className={className}
      style={{
        fontFamily: JOST,
        fontSize: size,
        fontWeight: 600,
        lineHeight: 1,
        color,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      tom
    </span>
  );
}
